import { Grid } from '@mui/material';
import GridContainer from 'components/GridContainer';
import TextField from 'components/FormElements/AppTextInput';
import FieldRow from 'components/FormElements/FieldRow';
import CkEditor from 'components/CkEditor';
import { stripTags } from 'hooks/form/useInvestorReportingForm';

import useStyles from '../styles';
import { Controller } from 'react-hook-form';
import { useState } from 'react';
import Spacing from 'components/Spacing';
import { Switch } from '@mui/material';

type Props = any;

const ClosedFund: React.FC<Props> = ({
  fund,
  index,
  isViewPage,
  control,
  setValue,
}) => {
  const [provideCommentary, setProvideCommentary] = useState(
    fund.provideCommentary,
  );
  const [shortDescriptionCharacterCount, setShortDescriptionCharacterCount] =
    useState(0);
  const shortDescriptionCharacterLimit = 220;
  const shortDescriptionCharacterError =
    shortDescriptionCharacterCount > shortDescriptionCharacterLimit;

  const { classes } = useStyles();

  return (
    <GridContainer>
      <Grid
        size={{
          md: 9,
          xs: 12,
        }}
      >
        <Spacing>
          <FieldRow key={`title-${index}`} title="Update title">
            <Controller
              name={`fundInformation[${index}][title]`}
              defaultValue={fund.title}
              control={control}
              render={({ field: { ref, ...rest }, formState: { errors } }) => (
                <TextField
                  {...rest}
                  inputRef={ref}
                  placeholder="e.g. Fund name - Q1 Quarterly Update"
                  fullWidth
                  error={!!errors?.fundInformation?.[index]?.title}
                  helperText={errors?.fundInformation?.[index]?.title?.message}
                  disabled={isViewPage}
                />
              )}
            />
          </FieldRow>
          <FieldRow
            title="Short description"
            informationText="This is a two line preview of the update that will show in each investor’s portal before they open the update."
            topTitle
          >
            <Controller
              name={`fundInformation[${index}][shortDescription]`}
              defaultValue={fund.shortDescription}
              control={control}
              render={({ field, formState: { errors } }) => (
                <>
                  <CkEditor
                    placeholder="A brief 30-35 word (220 character) preview of fund performance and what the update contains."
                    onChange={(value: string) => {
                      setShortDescriptionCharacterCount(
                        stripTags(value ?? '').length,
                      );

                      setValue(
                        `fundInformation[${index}][shortDescription]`,
                        value,
                      );
                    }}
                    value={field.value}
                    showLists={false}
                    disabled={isViewPage}
                    onBlur={(value: string) => {
                      setValue(
                        `fundInformation[${index}][shortDescription]`,
                        value,
                      );
                    }}
                  />
                  <p
                    className={classes.errorCk}
                    id={`fundInformation[${index}][shortDescription]`}
                  >
                    {
                      errors?.fundInformation?.[index]?.shortDescription
                        ?.message
                    }
                  </p>
                  {shortDescriptionCharacterCount > 0 && (
                    <p
                      className={`${classes.characterCount} ${
                        shortDescriptionCharacterError &&
                        classes.characterCountError
                      }`}
                    >
                      Character count: {shortDescriptionCharacterCount}{' '}
                      {shortDescriptionCharacterError &&
                        `(Character count must be ${shortDescriptionCharacterLimit} or less)`}
                    </p>
                  )}
                </>
              )}
            />
          </FieldRow>
          <FieldRow title="Provide commentary">
            <Controller
              name={`fundInformation[${index}][provideCommentary]`}
              defaultValue={!!fund.provideCommentary}
              control={control}
              render={({ field: { name, value } }) => (
                <Switch
                  name={name}
                  onChange={(event) => {
                    setValue(
                      `fundInformation[${index}][provideCommentary]`,
                      event.target.checked,
                    );
                    setProvideCommentary(event.target.checked);
                  }}
                  className={classes.switchCls}
                  checked={!!value}
                  disabled={isViewPage}
                />
              )}
            />
          </FieldRow>
          {provideCommentary ? (
            <>
              <FieldRow
                title="Fund manager commentary"
                informationText="This is your firm’s introduction, where you can provide background context to the update and discuss any other topics you feel relevant. It is attributed to the manager selected below."
                topTitle
              >
                <Controller
                  name={`fundInformation[${index}][commentary]`}
                  defaultValue={fund?.commentary}
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <>
                      <CkEditor
                        placeholder="A multi-paragraph overview of progress since the last update."
                        onChange={(value: string) => {
                          setValue(
                            `fundInformation[${index}][commentary]`,
                            value,
                          );
                        }}
                        value={field.value}
                        disabled={isViewPage}
                        onBlur={(value: string) => {
                          setValue(
                            `fundInformation[${index}][commentary]`,
                            value,
                          );
                        }}
                      />
                      <p
                        className={classes.errorCk}
                        id={`fundInformation[${index}][commentary]`}
                      >
                        {errors?.fundInformation?.[index]?.commentary?.message}
                      </p>
                    </>
                  )}
                />
              </FieldRow>
            </>
          ) : null}
        </Spacing>
      </Grid>
    </GridContainer>
  );
};

export default ClosedFund;
